import { downloadFile } from "@utils";
import { ACCEPTED_UPLOAD_TYPES } from "./components/file-upload-dialog/FileUploadDialog.consts";
import { ERROR_MESSAGES, PROGRESS_MODE, PROGRESS } from "./FileUpload.consts";

export const getAcceptObject = (acceptedTypes) => {
  const result = {};

  acceptedTypes.forEach(type => {
    const upperType = type.toUpperCase();
    if (ACCEPTED_UPLOAD_TYPES[upperType]) {
      Object.assign(result, ACCEPTED_UPLOAD_TYPES[upperType]);
    }
  });

  return result;
};

export const uploadDirectFileToStorage = async ({
  file,
  uploadDirectToStorage,
  setFileProgress,
  setErrors,
  progressMode = PROGRESS_MODE.MERGE
}) => {
  const result = await uploadDirectFilesToStorage({
    files: [file],
    uploadDirectToStorage,
    setFileProgress,
    setErrors,
    progressMode
  });

  return result ? result[0] : false;
};

export const uploadDirectFilesToStorage = async ({
  files,
  uploadDirectToStorage,
  setFileProgress,
  setErrors,
  progressMode = PROGRESS_MODE.MERGE
}) => {
  try {
    const fileNames = files.map(prepareBasicFileData).map(file => file.file_name);
    const initialProgress = fileNames.reduce((acc, name) => {
      acc[name] = PROGRESS.STARTED;
      return acc;
    }, {});

    if (progressMode === PROGRESS_MODE.MERGE) {
      setFileProgress(prev => ({ ...prev, ...initialProgress }));
    } else {
      setFileProgress(initialProgress);
    }

    await Promise.all(files.map((file, index) =>
      simulateProgress({ file: { ...file, file_name: fileNames[index] }, setFileProgress, progressMode })
    ));

    const result = await uploadDirectToStorage.mutateAsync({ files });

    // Set all files to 100% when complete
    const completedProgress = fileNames.reduce((acc, name) => {
      acc[name] = PROGRESS.ENDED;
      return acc;
    }, {});

    if (progressMode === PROGRESS_MODE.MERGE) {
      setFileProgress(prev => ({ ...prev, ...completedProgress }));
    } else {
      setFileProgress(completedProgress);
    }

    return result;
  } catch (error) {
    console.error("Error uploading files:", error);
    const newErrors = files.map(prepareBasicFileData).map(file => ({
      ...file,
      error: ERROR_MESSAGES.UPLOAD_FAILED
    }));
    if (progressMode === PROGRESS_MODE.MERGE) {
      setErrors(prev => [...prev, ...newErrors]);
    } else {
      setErrors(newErrors);
    }
    return false;
  }
};

export const prepareBasicFileData = (file) => {
  const [file_name, file_type] = file.name.split(".");
  return {
    file_name,
    file_type,
    upload_date: Date.now()
  };
};

export const handleDirectFileDownload = async ({
  getFileMutation,
  filePath,
  fileName = "download"
}) => {
  if (!filePath) return false;

  const fileUrl = await getFileMutation.mutateAsync({ filePath, fileName });
  if (fileUrl) {
    downloadFile(fileUrl, fileName);
    return true;
  }
  return false;
};

const simulateProgress = ({ file, setFileProgress, progressMode, maxSimulatedProgress = 100 }) => new Promise(resolve => {
  const fileSize = file.size / 1024;
  let duration;

  if (fileSize < 50) {
    duration = 500 + (fileSize * 10);
  } else if (fileSize < 500) {
    duration = 1000 + (fileSize * 2);
  } else {
    duration = Math.min(1500 + (fileSize / 8000), 5000);
  }

  const steps = 10;
  const interval = duration / steps;

  let step = 0;
  const timer = setInterval(() => {
    step++;
    const ratio = step / steps;
    const progress = Math.min(99.9, Math.max(0, Math.round(ratio * maxSimulatedProgress)));

    if (progressMode === PROGRESS_MODE.MERGE) {
      setFileProgress(prev => ({ ...prev, [file.file_name]: progress }));
    } else {
      setFileProgress({ [file.file_name]: progress });
    }

    if (step >= steps) {
      clearInterval(timer);
      resolve();
    }
  }, interval);
});

export const truncateFileName = ({ fileName, fileType, maxLength = 45 }) => {
  const extension = `.${fileType}`;
  const fullName = `${fileName}${extension}`;

  if (fullName.length <= maxLength) return fullName;

  const ellipsis = "..";
  const availableChars = maxLength - extension.length - ellipsis.length;

  if (availableChars <= 0) {
    return fullName.substring(0, maxLength - ellipsis.length) + ellipsis;
  }

  return `${fileName.substring(0, availableChars)}${ellipsis}${extension}`;
};
